import type { DocumentNode } from '@apollo/client';
import type { Maybe } from 'graphql/jsutils/Maybe';

import type {
  AuditAdV5,
  AuditLineItemV5,
  ChangeLogUnionObject,
  SimpleChangeValue,
  SingleValueChange,
} from '../../apis/graphql';
import type { Toggles } from '../ChangeLog/hooks/types';
import type { DefaultAudit } from '../ChangeLog/types';
import type { DayPartTimeRanges } from '../TimesList/types';

export type ChangeLogRecord = SingleValueChange;

export type ChangeLogRecordsList = ChangeLogRecord[];

export enum FieldNameTypes {
  id = 'id',
  name = 'name',
}
export type ChangeLogEntry = SingleValueChange;

export type NullableDayPart = {
  firstActiveHour: Maybe<number>;
  lastActiveHour: Maybe<number>;
  activeDayList: Array<string | null>;
};

export type ChangedDayPartRange = {
  day: string;
  oldRange: DayPartTimeRanges;
  newRange: DayPartTimeRanges;
};

export type ArrayGroupedChanges = {
  [key: string]: ChangeLogRecordsList;
};

export type ReduceGroupChangesByIndex = {
  notArrayChanges: ChangeLogRecordsList;
  arrayGroupedChanges: ArrayGroupedChanges;
};

export type GroupChangesByIndex = {
  audits: {
    notArrayChanges: ChangeLogRecordsList;
    arrayGroupedChanges: ArrayGroupedChanges;
  };
  creativeAudits: {
    notArrayChanges: ChangeLogRecordsList;
    arrayGroupedChanges: ArrayGroupedChanges;
  }[];
};

export type AdChangeInLineItem = {
  name: string;
  id: string;
};

export interface UseNotificationsChangeLogProps {
  GET_NOTIFICATIONS: DocumentNode;
  variables: Variables;
}

export type JoinedTermsArray = { dimensionId: string; valuesIds: string[]; not: boolean }[];

export type ChangeLogUnionObjectWithTerms<
  Object extends 'AuditLineItemV5' | 'AuditAdV5'
> = Object extends 'AuditLineItemV5'
  ? AuditLineItemV5 & { lineItemTargetingTermValues: AuditLineItemV5['targetingTermValues'] }
  : AuditAdV5 & { adTargetingTermValues: AuditAdV5['targetingTermValues'] };

export interface AcknowledgeOmsChangesProps {
  name: string;
  id: string;
  onCancel: () => void;
}
export interface Variables {
  filterOptions: {
    campaignIdList: string[];
    isAcknowledged: boolean;
  };
  name?: string;
  selectedTab: string;
}

export interface BaseNotificationsProps {
  loading: boolean;
  toggles: Toggles[];
  toggleAt: (index: number, value: boolean | boolean[]) => void;
  toggleAll: (isOpen: boolean | boolean[]) => void;
  filteredLogsToShow: FilteredChangeLogEntry[];
  toggleCheckedAt: (id: string, isChecked: boolean) => void;
  toggleCheckedAll: (isChecked: boolean) => void;
  checkedIds: string[];
  onNext: () => void;
  hasMore: boolean;
  initialLoading?: boolean;
}

export interface ChangeLogListProps {
  toggles: Toggles[];
  toggleAt: (index: number, value: boolean | boolean[]) => void;
  loading?: boolean;
  entityType?: string;
  initialLoading?: boolean;
  filteredLogsToShow?: FilteredChangeLogEntry[];
  hasMore?: boolean;
  onNext?: () => void;
  toggleCheckedAt: (id: string, isChecked: boolean) => void;
  checkedIds: string[];
}

export interface NotificationsChangeLogProps {
  name?: string;
  selectedTab?: string;
  loading: boolean;
  toggles: Toggles[];
  toggleAt: (index: number, value: boolean | boolean[]) => void;
  filteredLogsToShow: FilteredChangeLogEntry[];
  toggleCheckedAt: (id: string, isChecked: boolean) => void;
  toggleCheckedAll: (isChecked: boolean) => void;
  checkedIds: string[];
  onNext: () => void;
  hasMore: boolean;
  initialLoading?: boolean;
}

export interface UseNotificationsChangeLogResult extends BaseNotificationsProps {
  loadingFilter: boolean;
  eventCounts?: {
    campaigns: number;
    lines: number;
    orders: number;
  };
  submitAcknowledgements: (options: { onSuccess: () => void }) => Promise<void>;
  isSubmitting: boolean;
}

export interface NotificationsLogListEntryProps {
  audits?: DefaultAudit;
  id: string;
  dateFormat: string;
  modifiedBy: string | null;
  originalIndex: number;
  toggles?: Toggles[];
  toggleAt: (index: number, value: boolean | boolean[]) => void;
  toggleCheckedAt: (id: string, isChecked: boolean) => void;
  checkedIds: string[];
}
export type { SimpleChangeValue, SingleValueChange };

export interface ChangeLogNotificationEntry {
  id: string;
  modifiedBy: string;
  modifiedDate: string;
  acknowledgement: {
    acknowledgedBy: string;
    acknowledgedDate: string;
    __typename: string;
  } | null;
  changeList: SingleValueChange[];
  object: ChangeLogUnionObject;
  correlationId: string | null;
  __typename: string;
}

export interface NotificationNode {
  id: string;
  notificationDate: string;
  omsLinkId: string;
  orderId: string;
  campaignId: string;
  acknowledgement: null;
  accountManager: string;
  events: ChangeLogNotificationEntry[];
  __typename: string;
}

export interface NotificationEdge {
  node: NotificationNode;
  __typename: string;
}

export interface GetNotifications {
  edges: NotificationEdge[];
  __typename: string;
}

export interface GetNotificationsData {
  getNotifications: GetNotifications;
}

export interface GetNotificationsResponse {
  data: GetNotificationsData;
}

export interface GroupedChanges {
  notArrayChanges: ChangeLogEntry[];
  arrayGroupedChanges: Record<string, ChangeLogEntry[]>;
}

export interface AuditProps {
  changes: ChangeLogEntry[];
  subHeaderTitle: string;
  isLineThrough: boolean;
}

export interface NormalizedChanges {
  audits: {
    tablesProps: AuditProps[];
    changesPlural: string;
    changesLength: number;
  };
}

export interface ChangeLogNode {
  id: string;
  modifiedBy: string | null;
  modifiedDate: string;
  object: ChangeLogUnionObject;
  changeList: ChangeLogEntry[];
}

export interface FilteredChangeLogEntry {
  audits: NormalizedChanges['audits'];
  id: string;
  dateFormat: string;
  modifiedBy: string | null;
  originalIndex: number;
}
